import { PaginationPayload } from "react-admin";

import { ManagerInvitation } from "../generated/graphql";
import { DeviceRecord } from "../types/device";
import { LocationRecord } from "../types/location";
import { OrganizationRecord } from "../types/organization";
import { PatientRecord } from "../types/patient";
import { PractitionerRecord } from "../types/practitioner";

type SearchFields =
  | (keyof PractitionerRecord)[]
  | (keyof PatientRecord)[]
  | (keyof LocationRecord)[]
  | (keyof OrganizationRecord)[]
  | (keyof DeviceRecord)[]
  | (keyof ManagerInvitation)[];

type FilterResult<T> = {
  data: T[];
  total: number;
};

export function search<T extends { id: string | number }>(data: T[], searchTerm: string, fields: SearchFields): T[] {
  if (!searchTerm.trim()) return data;

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  return data.filter(item => {
    for (const field of fields) {
      const fieldValue = item[field as keyof T];
      if (fieldValue == null) continue;

      const stringValue = String(fieldValue).toLowerCase();
      if (stringValue.includes(normalizedSearchTerm)) return true;
    }
    return false;
  });
}

export function paginate<T>(data: T[], pagination: PaginationPayload): FilterResult<T> {
  const startIndex = (pagination.page - 1) * pagination.perPage;
  const endIndex = startIndex + pagination.perPage;
  const paginatedData = data.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    total: data.length,
  };
}

export function searchAndPaginate<T extends { id: string | number }>(
  data: T[],
  pagination: PaginationPayload,
  searchTerm: string | undefined,
  fields: SearchFields
): FilterResult<T> {
  if (!searchTerm) {
    return paginate(data, pagination);
  }

  const filteredData = search(data, searchTerm, fields);
  return paginate(filteredData, pagination);
}
